import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import { Plus, Search, Filter, Ticket, AlertCircle, Clock, CheckCircle2, ArrowUpRight, ChevronDown, Tag } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  fetchTickets,
  fetchTicketStats,
  fetchAllTags,
  type SupportTicket,
  type TicketStats,
  type TicketTag,
} from '@/lib/api/support-tickets';

const STATUS_OPTIONS = [
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'escalated', label: 'Escalated' },
  { value: 'resolved', label: 'Resolved' },
  { value: 'closed', label: 'Closed' },
];

const DEFAULT_STATUSES = ['open', 'in_progress', 'escalated'];

const PRIORITY_OPTIONS = [
  { value: 'all', label: 'All Priority' },
  { value: 'low', label: 'Low' },
  { value: 'normal', label: 'Normal' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
];

function StatusBadge({ status }: { status: string }) {
  const variants: Record<string, string> = {
    open: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
    in_progress: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
    escalated: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
    resolved: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
    closed: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
  };

  const labels: Record<string, string> = {
    open: 'Open',
    in_progress: 'In Progress',
    escalated: 'Escalated',
    resolved: 'Resolved',
    closed: 'Closed',
  };

  return (
    <Badge className={variants[status] || variants.open} variant="secondary">
      {labels[status] || status}
    </Badge>
  );
}

function PriorityBadge({ priority }: { priority: string }) {
  const variants: Record<string, string> = {
    low: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
    normal: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
    high: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300',
    urgent: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
  };

  return (
    <Badge className={variants[priority] || variants.normal} variant="secondary">
      {priority.charAt(0).toUpperCase() + priority.slice(1)}
    </Badge>
  );
}

function StatCard({
  title,
  value,
  icon: Icon,
  className,
}: {
  title: string;
  value: number;
  icon: React.ElementType;
  className?: string;
}) {
  return (
    <Card className={className}>
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-muted-foreground">{title}</p>
            <p className="text-2xl font-bold">{value}</p>
          </div>
          <Icon className="h-8 w-8 text-muted-foreground" />
        </div>
      </CardContent>
    </Card>
  );
}

function StatusMultiSelect({
  selectedStatuses,
  onStatusChange,
}: {
  selectedStatuses: string[];
  onStatusChange: (statuses: string[]) => void;
}) {
  const [open, setOpen] = useState(false);

  const handleStatusToggle = (statusValue: string) => {
    if (selectedStatuses.includes(statusValue)) {
      // Remove status (but ensure at least one remains)
      if (selectedStatuses.length > 1) {
        onStatusChange(selectedStatuses.filter((s) => s !== statusValue));
      }
    } else {
      // Add status
      onStatusChange([...selectedStatuses, statusValue]);
    }
  };

  const handleSelectAll = () => {
    onStatusChange(STATUS_OPTIONS.map((opt) => opt.value));
  };

  const handleReset = () => {
    onStatusChange(DEFAULT_STATUSES);
  };

  // Generate display text for trigger
  const getDisplayText = () => {
    if (selectedStatuses.length === STATUS_OPTIONS.length) {
      return 'All Statuses';
    }
    if (selectedStatuses.length === 0) {
      return 'Select Status';
    }
    if (selectedStatuses.length <= 2) {
      return selectedStatuses
        .map((s) => STATUS_OPTIONS.find((opt) => opt.value === s)?.label)
        .join(', ');
    }
    return `${selectedStatuses.length} statuses`;
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full md:w-48 justify-between"
        >
          <span className="truncate">{getDisplayText()}</span>
          <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-56 p-0" align="start">
        <div className="p-2 space-y-2">
          {/* Quick actions */}
          <div className="flex justify-between text-xs text-muted-foreground px-2 pb-2 border-b">
            <button
              type="button"
              onClick={handleSelectAll}
              className="hover:text-foreground transition-colors"
            >
              Select All
            </button>
            <button
              type="button"
              onClick={handleReset}
              className="hover:text-foreground transition-colors"
            >
              Reset
            </button>
          </div>

          {/* Checkbox list */}
          <div className="space-y-1">
            {STATUS_OPTIONS.map((option) => (
              <div
                key={option.value}
                className="flex items-center space-x-2 px-2 py-1.5 rounded-sm hover:bg-accent cursor-pointer"
                onClick={() => handleStatusToggle(option.value)}
              >
                <Checkbox
                  id={`status-${option.value}`}
                  checked={selectedStatuses.includes(option.value)}
                  onCheckedChange={() => handleStatusToggle(option.value)}
                />
                <label
                  htmlFor={`status-${option.value}`}
                  className="flex-1 cursor-pointer text-sm font-normal"
                >
                  {option.label}
                </label>
              </div>
            ))}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default function SupportTicketsPage() {
  const [, setLocation] = useLocation();
  const [search, setSearch] = useState('');
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>(DEFAULT_STATUSES);
  const [priority, setPriority] = useState('all');
  const [selectedTagFilters, setSelectedTagFilters] = useState<number[]>([]);
  const [page, setPage] = useState(1);

  const { data: statsData } = useQuery({
    queryKey: ['support-ticket-stats'],
    queryFn: fetchTicketStats,
  });

  const { data: allTags = [] } = useQuery({
    queryKey: ['support-ticket-tags'],
    queryFn: () => fetchAllTags(),
  });

  const { data: ticketsData, isLoading } = useQuery({
    queryKey: ['support-tickets', { page, search, statuses: selectedStatuses, priority, tags: selectedTagFilters }],
    queryFn: () =>
      fetchTickets({
        page,
        limit: 20,
        search: search || undefined,
        statuses: selectedStatuses.length > 0 ? selectedStatuses : undefined,
        priority: priority !== 'all' ? priority : undefined,
        tags: selectedTagFilters.length > 0 ? selectedTagFilters : undefined,
      }),
  });

  const stats = statsData || { open: 0, inProgress: 0, escalated: 0, resolvedThisMonth: 0 };
  const tickets = ticketsData?.data || [];
  const pagination = ticketsData?.pagination;

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Support Tickets</h1>
          <p className="text-muted-foreground">
            Track and manage customer support issues
          </p>
        </div>
        <Button onClick={() => setLocation('/support-tickets/new')}>
          <Plus className="h-4 w-4 mr-2" />
          New Ticket
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard
          title="Open Tickets"
          value={stats.open}
          icon={Ticket}
        />
        <StatCard
          title="In Progress"
          value={stats.inProgress}
          icon={Clock}
        />
        <StatCard
          title="Escalated"
          value={stats.escalated}
          icon={AlertCircle}
          className="border-red-200 dark:border-red-900"
        />
        <StatCard
          title="Resolved This Month"
          value={stats.resolvedThisMonth}
          icon={CheckCircle2}
        />
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search tickets..."
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  className="pl-10"
                />
              </div>
            </div>
            <StatusMultiSelect
              selectedStatuses={selectedStatuses}
              onStatusChange={(statuses) => {
                setSelectedStatuses(statuses);
                setPage(1);
              }}
            />
            <Select
              value={priority}
              onValueChange={(val) => {
                setPriority(val);
                setPage(1);
              }}
            >
              <SelectTrigger className="w-full md:w-40">
                <SelectValue placeholder="Priority" />
              </SelectTrigger>
              <SelectContent>
                {PRIORITY_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {allTags.length > 0 && (
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full md:w-40 justify-between">
                    <Tag className="h-4 w-4 mr-2" />
                    <span className="truncate">
                      {selectedTagFilters.length === 0
                        ? 'Tags'
                        : `${selectedTagFilters.length} tag${selectedTagFilters.length > 1 ? 's' : ''}`}
                    </span>
                    <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-56 p-0" align="start">
                  <div className="p-2 space-y-2">
                    {selectedTagFilters.length > 0 && (
                      <div className="flex justify-end text-xs text-muted-foreground px-2 pb-2 border-b">
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedTagFilters([]);
                            setPage(1);
                          }}
                          className="hover:text-foreground transition-colors"
                        >
                          Clear
                        </button>
                      </div>
                    )}
                    <div className="space-y-1">
                      {allTags.map((tag) => (
                        <div
                          key={tag.id}
                          className="flex items-center space-x-2 px-2 py-1.5 rounded-sm hover:bg-accent cursor-pointer"
                          onClick={() => {
                            setSelectedTagFilters((prev) =>
                              prev.includes(tag.id)
                                ? prev.filter((id) => id !== tag.id)
                                : [...prev, tag.id]
                            );
                            setPage(1);
                          }}
                        >
                          <Checkbox
                            checked={selectedTagFilters.includes(tag.id)}
                            onCheckedChange={() => {
                              setSelectedTagFilters((prev) =>
                                prev.includes(tag.id)
                                  ? prev.filter((id) => id !== tag.id)
                                  : [...prev, tag.id]
                              );
                              setPage(1);
                            }}
                          />
                          <Badge
                            className="text-xs text-white"
                            style={{ backgroundColor: tag.color || '#6b7280' }}
                          >
                            {tag.name}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Tickets Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-8 text-center text-muted-foreground">
              Loading tickets...
            </div>
          ) : tickets.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              No tickets found
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Ticket</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Provider</TableHead>
                  <TableHead>Issue</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Priority</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tickets.map((ticket) => (
                  <TableRow
                    key={ticket.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => setLocation(`/support-tickets/${ticket.id}`)}
                  >
                    <TableCell className="font-medium">
                      {ticket.ticketNumber}
                    </TableCell>
                    <TableCell>
                      {ticket.displayCustomerName || ticket.customerName || '-'}
                    </TableCell>
                    <TableCell>{ticket.systemProvider || '-'}</TableCell>
                    <TableCell className="max-w-xs">
                      <div className="truncate">{ticket.issueDescription}</div>
                      {ticket.tags && ticket.tags.length > 0 && (
                        <div className="flex gap-1 mt-1 flex-wrap">
                          {ticket.tags.map((tag) => (
                            <span
                              key={tag.id}
                              className="inline-block px-1.5 py-0.5 rounded-full text-[10px] font-medium text-white"
                              style={{ backgroundColor: tag.color || '#6b7280' }}
                            >
                              {tag.name}
                            </span>
                          ))}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={ticket.status} />
                    </TableCell>
                    <TableCell>
                      <PriorityBadge priority={ticket.priority} />
                    </TableCell>
                    <TableCell>{formatDate(ticket.createdAt)}</TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm">
                        <ArrowUpRight className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      {pagination && pagination.totalPages > 1 && (
        <div className="flex justify-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={!pagination.hasPrevious}
            onClick={() => setPage(page - 1)}
          >
            Previous
          </Button>
          <span className="flex items-center px-4 text-sm text-muted-foreground">
            Page {pagination.page} of {pagination.totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={!pagination.hasNext}
            onClick={() => setPage(page + 1)}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
}
